// pages/app/matches/(components)/overview-table.tsx
import * as React from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/default-input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useTRPC } from "@/trpc";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import * as Icons from "lucide-react";
import { useFormContext } from "react-hook-form";
import { MatchFilterState, normalizeFilterInput } from "../(context)/filter";
import { ROUTES_CONFIG } from "@/config/routes.config";
import { useNavigate } from "react-router-dom";
import { formatDate } from "@/lib/date";

type Props = { onRefetchReady?: (fn: () => void) => void };

const OverviewTable = ({ onRefetchReady }: Props) => {
  const navigate = useNavigate();
  const { trpc } = useTRPC();
  const { watch, getValues, setValue, reset } = useFormContext<MatchFilterState>();
  const filterState = watch();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["matches", filterState],
    queryFn: async () => {
      const input = normalizeFilterInput(getValues());
      return trpc.matches.search.query(input);
    },
    enabled: true,
  });

  const handleInvalidate = () => {
    qc.invalidateQueries({ queryKey: ["matches"] });
  };

  React.useEffect(() => {
    const savedFilters = qc.getQueryData<MatchFilterState>(["matchFilters"]);
    if (savedFilters) {
      reset(savedFilters);
      qc.removeQueries({ queryKey: ["matchFilters"] }); 
    }
  }, [reset, qc]);
  React.useEffect(() => {
    onRefetchReady?.(handleInvalidate);
  }, [onRefetchReady, qc]);

  return (
    <>
    <div className="h-full overflow-auto">
      <div className="flex flex-col justify-between gap-4 h-full">
        <div className="bg-foreground/10 rounded-md border pb-2 pr-2 pl-2 overflow-auto h-full">
          <Table className="overflowclip w-full relative" divClassname="overflow-y-scroll max-h-[100%]">
            <TableHeader className="sticky top-0 w-full border-b bg-sidebar/50 backdrop-blur-md z-10">
              <TableRow>
                <TableHead className="w-[150px]">Datum</TableHead>
                <TableHead className="text-end">Thuis</TableHead>
                <TableHead className="text-center">Score</TableHead>
                <TableHead className="w-[20%]">Uit</TableHead>
                <TableHead className="w-[300px]">Competitie</TableHead>
                <TableHead>Land</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {query?.data?.matches?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-sm text-foreground/50 p-4">
                    Geen wedstrijden gevonden voor volgende filters:
                    <br />
                    Algemeen zoekveld: "{filterState.search ?? "Niet aangegeven"}", 
                    Competitie: "{filterState.competition?.competition_name ?? "Niet aangegeven"}", 
                    Club: "{filterState.competition?.club_name ?? "Niet aangegeven"}", 
                    Datum van: "{filterState.daterange?.date_from_display ?? "Niet aangegeven"}" tot "{filterState.daterange?.date_to_display ?? "Niet aangegeven"}"
                  </TableCell>
                </TableRow>
              ) : (
                query?.data?.matches?.map((m) => {
                const hs = m?.home_score;
                const as = m?.away_score;

                const homeClub = m.home?.club;
                const awayClub = m.away?.club;

                const matchDetailPath =
                  ROUTES_CONFIG.APP.MATCH_DETAIL?.$buildPath?.({ params: { match_id: m.id } }) ??
                  `/app/matches/${m.id}`;

                return (
                  <TableRow onClick={() => {
                        qc.setQueryData(["matchFilters"], getValues());
                        navigate(matchDetailPath);
                      }}
                      key={m.id}
                      className="hover:bg-foreground/5 group/row cursor-pointer">
                    <TableCell>
                      {m?.scheduled_at ? formatDate(m.scheduled_at) : "–"}
                    </TableCell>

                    <TableCell
                      className="group/name cursor-pointer text-right"
                    >
                      <div className="flex flex-row-reverse items-center gap-2 min-w-0">
                        <Avatar className="size-6">
                          <AvatarImage
                            src={homeClub?.crest_url ?? undefined}
                            alt={homeClub?.name ?? undefined}
                          />
                          <AvatarFallback>{homeClub?.name?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <p className="truncate font-medium">
                            {homeClub?.name || "–"}
                          </p>
                          {m.home?.starting_formation ? (
                            <p className="text-xs text-muted-foreground truncate">
                              opstelling: {m.home.starting_formation}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </TableCell>

                    <TableCell className="text-center">
                      <div className="inline-flex items-center gap-2">
                        <span className="tabular-nums font-semibold">
                          {Number.isFinite(hs as number) ? hs : "–"} : {Number.isFinite(as as number) ? as : "–"}
                        </span>
                      </div>
                    </TableCell>

                    {/* AWAY */}
                    <TableCell
                      className="group/name cursor-pointer"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <Avatar className="size-6">
                          <AvatarImage
                            src={awayClub?.crest_url ?? undefined}
                            alt={awayClub?.name ?? undefined}
                          />
                          <AvatarFallback>{awayClub?.name?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <p className="truncate font-medium">
                            {awayClub?.name || "–"}
                          </p>
                          {m.away?.starting_formation ? (
                            <p className="text-xs text-muted-foreground truncate">
                              opstelling: {m.away.starting_formation}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{m?.iteration?.competition?.name || "–"}</TableCell>
                    <TableCell>{m?.iteration?.competition?.country_iso3 || "–"}</TableCell>
                  </TableRow>
                );
              })
            )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination footer */}
        <div className="flex items-center justify-between gap-4">
          <p className="text-sm font-medium text-foreground/50">
            {query?.data?.total} rijen gevonden
          </p>

          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              onClick={() => setValue("page", 1)}
              disabled={Number(filterState?.page ?? 0) <= 1}
            >
              <Icons.ChevronsLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              onClick={() => setValue("page", Math.max(1, Number(filterState.page) - 1))}
              disabled={Number(filterState?.page ?? 0) <= 1}
            >
              <Icons.ChevronLeft className="w-4 h-4" />
            </Button>
            <Input
              value={filterState.page}
              onChange={(e) => setValue("page", e.currentTarget.valueAsNumber)}
              onBlur={() => { query.refetch(); }}
              type="number"
              min={1}
              max={query?.data?.pages || 1}
            />
            <Button
              variant="outline"
              onClick={() =>
                setValue(
                  "page",
                  Math.min(Number(query?.data?.pages || 1), Number(filterState.page) + 1)
                )
              }
              disabled={Number(filterState.page) >= Number(query?.data?.pages)}
            >
              <Icons.ChevronRight className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              onClick={() => setValue("page", query?.data?.pages || 1)}
              disabled={Number(filterState.page) >= Number(query?.data?.pages)}
            >
              <Icons.ChevronsRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  </>
  );
};

export default OverviewTable;
